/**
 * Copyright 2019, OpenCensus Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { NoHeadersPropagation } from '../propagation/no_headers_propagation';
import { AlwaysSampler } from '../sampler/sampler';
import { RootSpan } from './root-span';
import { Span } from './span';
var NO_HEADERS_PROPAGATION = new NoHeadersPropagation();
/** TracerBase represents a tracer */
var TracerBase = /** @class */ (function () {
    function TracerBase() {
        /**
         * A sampler used to make trace sample decisions. In the case of
         * opencensus-web, ultimate sampling decisions will likely be made by the
         * server or agent/collector. So this defaults to sampling every trace.
         */
        this.sampler = new AlwaysSampler();
        /** An object to log information to. Logs to the JS console by default. */
        this.logger = console;
        /** Trace context header propagation behavior. */
        this.propagation = NO_HEADERS_PROPAGATION;
        /** Event listeners for spans managed by the tracer. */
        this.eventListeners = [];
        /**
         * Active status from tracer instance - this is always true for
         * opencensus-web for code simplicity purposes.
         */
        this.active = true;
        /**
         * Trace parameter configuration. Not used by OpenCensus Web, but
         * kept for interface compatibility with @opencensus/web-types.
         */
        this.activeTraceParams = {};
        /** A configuration for starting the tracer */
        this.config = {};
    }
    /**
     * Starts the tracer. This makes the tracer active and sets `logger` and
     * `propagation` based on the given config. The `samplingRate` property of
     * `config` is currently ignored.
     */
    TracerBase.prototype.start = function (config) {
        this.logger = config.logger || console;
        this.propagation = config.propagation || NO_HEADERS_PROPAGATION;
        this.config = config;
        return this;
    };
    /** Stops the tracer. This is a no-op with opencensus-web. */
    TracerBase.prototype.stop = function () {
        return this;
    };
    /**
     * Start a new RootSpan to currentRootSpan. Currently no sampling decisions are propagated or made here.
     * @param options Options for tracer instance
     * @param fn Callback function
     * @returns The callback return
     */
    TracerBase.prototype.startRootSpan = function (options, fn) {
        var rootSpan = new RootSpan(this, options);
        // Add default attributes
        var defaultAttributes = this.config && this.config.defaultAttributes;
        if (defaultAttributes) {
            for (var _i = 0, _a = Object.keys(defaultAttributes); _i < _a.length; _i++) {
                var key = _a[_i];
                rootSpan.addAttribute(key, defaultAttributes[key]);
            }
        }
        rootSpan.start();
        return fn(rootSpan);
    };
    /** Notifies listeners of the span start. */
    TracerBase.prototype.onStartSpan = function (root) {
        for (var _i = 0, _a = this.eventListeners; _i < _a.length; _i++) {
            var listener = _a[_i];
            listener.onStartSpan(root);
        }
    };
    /** Notifies listeners of the span end. */
    TracerBase.prototype.onEndSpan = function (root) {
        for (var _i = 0, _a = this.eventListeners; _i < _a.length; _i++) {
            var listener = _a[_i];
            listener.onEndSpan(root);
        }
    };
    TracerBase.prototype.registerSpanEventListener = function (listener) {
        this.eventListeners.push(listener);
    };
    TracerBase.prototype.unregisterSpanEventListener = function (listener) {
        this.eventListeners = this.eventListeners.filter(function (l) { return l !== listener; });
    };
    /**
     * Start a new Span.
     * @param name SpanOptions object.
     * @returns The new Span instance started
     */
    TracerBase.prototype.startChildSpan = function (options) {
        var rootSpan = new Span();
        if (options && options.childOf) {
            rootSpan = options.childOf;
        }
        var span = rootSpan.startChildSpan(options);
        // Add default attributes
        var defaultAttributes = this.config && this.config.defaultAttributes;
        if (defaultAttributes) {
            for (var _i = 0, _a = Object.keys(defaultAttributes); _i < _a.length; _i++) {
                var key = _a[_i];
                rootSpan.addAttribute(key, defaultAttributes[key]);
            }
        }
        return span;
    };
    /** Sets the current root span. */
    TracerBase.prototype.setCurrentRootSpan = function (root) {
        // no-op, this is only required in case of tracer with cls.
    };
    return TracerBase;
}());
export { TracerBase };
//# sourceMappingURL=tracer-base.js.map